import { z } from "zod";

/**
 * Activation funnel — ordered milestones computed from data existence
 * (never manual check-offs). Single source of truth for predicates:
 * `apps/api/src/modules/lifecycle/activation.ts`.
 */
export const activationMilestoneKeySchema = z.enum([
  "SIGNED_UP",
  "BUSINESS_CREATED",
  "SERVICE_ADDED",
  "HOURS_SET",
  "BOOKABLE",
  "TEST_BOOKING",
  "PAYMENTS_CONNECTED",
  "LINK_SHARED",
  "FIRST_BOOKING",
  // A real customer's visit checked out in Openings, by any method (card or
  // a manually recorded cash/other payment). Oct 2026.
  "FIRST_CHECKOUT",
  "FIRST_PAYOUT",
]);
export type ActivationMilestoneKey = z.infer<
  typeof activationMilestoneKeySchema
>;

export const activationMilestoneSchema = z.object({
  key: activationMilestoneKeySchema,
  complete: z.boolean(),
  /** Optional milestones (e.g. PAYMENTS_CONNECTED) don't gate setup completion. */
  optional: z.boolean(),
});
export type ActivationMilestone = z.infer<typeof activationMilestoneSchema>;

export const activationResponseSchema = z.object({
  /** All milestones in funnel order. */
  milestones: z.array(activationMilestoneSchema),
  /**
   * True when every non-optional SETUP milestone (through LINK_SHARED) is
   * complete — the dashboard checklist auto-hides on this.
   */
  setupComplete: z.boolean(),
  /**
   * EducationProgress keys recorded for this user + salon (e.g.
   * "checklist:dismissed", "event:link_copied", "tour:appointments").
   */
  progressKeys: z.array(z.string()),
});
export type ActivationResponse = z.infer<typeof activationResponseSchema>;

/** Body for recording a per-user education progress key (idempotent). */
export const educationProgressCreateSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9_:-]+$/i, "Invalid progress key"),
});
export type EducationProgressCreateInput = z.infer<
  typeof educationProgressCreateSchema
>;

export const educationProgressCreateResponseSchema = z.object({
  key: z.string(),
  created: z.boolean(),
});
export type EducationProgressCreateResponse = z.infer<
  typeof educationProgressCreateResponseSchema
>;

/**
 * Response for deleting a per-user education progress key (idempotent —
 * `deleted: false` when the key wasn't recorded). Used by preference-style
 * keys like `pref:nav_expanded` that toggle off.
 */
export const educationProgressDeleteResponseSchema = z.object({
  key: z.string(),
  deleted: z.boolean(),
});
export type EducationProgressDeleteResponse = z.infer<
  typeof educationProgressDeleteResponseSchema
>;
